import { Component, inject, OnInit, signal, WritableSignal, ChangeDetectionStrategy } from "@angular/core";
import { Table, TableModule } from "@openng/optimus-ui/table";

import { SvgIconComponent } from "../../../../shared/components/svg-icon/svg-icon.component";
import { concatMap, first, of } from "rxjs";
import { AssetService } from "../../../../services/api/asset/asset.service";
import { MultiSelectModule } from "@openng/optimus-ui/multiselect";
import { FormsModule } from "@angular/forms";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { ASSET_TYPES } from "../../../../enums/assetTypes.enum";
import { CustomerComponent } from "../forms/customer/customer.component";
import { DialogService, DynamicDialogRef } from "@openng/optimus-ui/dynamicdialog";
import { YesNoPipe } from "../../../../shared/pipes/yesNo/yes-no.pipe";
import { ConfirmationService } from "@openng/optimus-ui/api";
import { IconFieldModule } from "@openng/optimus-ui/iconfield";
import { InputIconModule } from "@openng/optimus-ui/inputicon";
import { InputTextModule } from "@openng/optimus-ui/inputtext";

import { UserListComponent } from "../user-list/user-list.component";
import { DocumentListItemComponent } from "../../../../shared/components/document-list-item/document-list-item.component";
import { MediaService } from "../../../../services/api/media/media.service";
import { PermissionService } from "../../../../services/api/permission/permission.service";
import { HasPermissionDirective } from "../../../../shared/directives/has-permission/has-permission.directive";

import { Protocol } from "../../../../models/ui/protocol";
import { TooltipModule } from "@openng/optimus-ui/tooltip";
import { SimplebarAngularModule } from "simplebar-angular";
import { AuthService } from "../../../../services/api/auth/auth.service";
import { BotevoAsset, BotevoAssetCompact } from "../../../../models/api/asset/botevoAsset";
import { Customer } from "../../../../models/api/asset/customer";
import { User } from "../../../../models/api/user";
import { Column } from "../../../../models/ui/tableColumn";
import { TenantCompact } from "../../../../models/api/asset/assetCompact";
import { TenantSettingsComponent } from "../forms/tenant-settings/tenant-settings.component";
import { TenantAdministrationService } from "../../../../services/api/tenant-administration/tenant-administration.service";
import { ProgressSpinnerModule } from "@openng/optimus-ui/progressspinner";
import { createTimeoutSignal } from "../../../../shared/utils/timeout.utils";
import { TimeoutComponent } from "../../../../shared/components/timeout/timeout.component";

export const CUSTOMER_COLS: Column[] = [
  {
    field: "label",
    header: "gate_administration.asset_table.label",
  },
  {
    field: "notes",
    header: "gate_administration.asset_table.notes",
  },
];

@Component({
  standalone: true,
  selector: "app-tenant-details",
  imports: [
    TableModule,
    MultiSelectModule,
    FormsModule,
    TranslateModule,
    SvgIconComponent,
    YesNoPipe,
    InputIconModule,
    IconFieldModule,
    InputTextModule,
    DocumentListItemComponent,
    HasPermissionDirective,
    TooltipModule,
    SimplebarAngularModule,
    ProgressSpinnerModule,
    TimeoutComponent,
  ],
  templateUrl: "./tenant-details.component.html",
  styleUrl: "./tenant-details.component.scss",
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [],
})
export class TenantDetailsComponent implements OnInit {
  public cols!: Column[];
  public selectedCols!: Column[];
  public assets: WritableSignal<BotevoAssetCompact[] | null> = signal(null);
  public permissions = inject(PermissionService);
  public translate = inject(TranslateService);
  public protocols: WritableSignal<Protocol[]> = signal([]);
  private ref: DynamicDialogRef | null = null;
  private assetService = inject(AssetService);
  private dialogService = inject(DialogService);
  private confirmationService = inject(ConfirmationService);
  private authService = inject(AuthService);
  private mediaService = inject(MediaService);
  private tenantService = inject(TenantAdministrationService);
  private tenant!: TenantCompact;
  public loading = signal(true);
  public timeOut = createTimeoutSignal(this.loading);

  ngOnInit(): void {
    this.cols = CUSTOMER_COLS;
    this.selectedCols = CUSTOMER_COLS;
    this.setCustomers();
    this.getProtocols();
  }

  /**
   * Fetches customers from the backend and assigns them to the assets property sorted by label.
   * @param refresh {boolean} Whether to refresh the data from the backend. Defaults to false.
   */
  public setCustomers(refresh = false) {
    this.loading.set(true);
    this.assetService
      .getAssets(ASSET_TYPES.CUSTOMER, undefined, undefined, refresh)
      .pipe(first())
      .subscribe({
        next: (customers) => {
          if (customers) {
            this.assets.set(
              customers.sort((a: BotevoAssetCompact, b: BotevoAssetCompact) => {
                const aLabel = a.label;
                const bLabel = b.label;
                if (aLabel && bLabel) {
                  return aLabel.localeCompare(bLabel);
                } else {
                  return 0;
                }
              })
            );
          }
          this.loading.set(false);
        },
        error: () => {
          this.loading.set(false);
        },
      });
  }

  /**
   * Return an array of column fields for filtering
   * @param columns The columns to get the fields from.
   * @returns An array of fields
   */
  public getFilterFields(columns: Column[]): string[] | undefined {
    return columns.map((column) => {
      return column.field;
    });
  }

  /**
   * Opens a dialog to create a new customer asset.
   * Fetches an empty customer asset from the asset service, then opens the asset form
   * in 'add' mode to allow the user to input new customer details.
   */

  public onCreateClick() {
    this.assetService
      .getEmptyAsset(ASSET_TYPES.CUSTOMER)
      .pipe(first())
      .subscribe({
        next: (asset) => {
          if (asset) {
            this.openAssetForm(asset, "add");
          }
        },
      });
  }

  /**
   * Opens a dialog to edit an existing customer asset.
   * Retrieves the customer asset details from the asset service using the given customer ID,
   * then opens the asset form in 'edit' mode to allow the user to modify customer details.
   * @param customerId The ID of the customer to edit.
   */
  public onEditClick(customerId: string) {
    this.assetService.getAssetDetails(customerId, ASSET_TYPES.CUSTOMER).subscribe({
      next: (asset) => {
        if (asset) {
          this.openAssetForm(asset, "edit");
        }
      },
    });
  }

  /**
   * Filters the table using the input from the user.
   * The filter is done using the "contains" filter match mode.
   * @param dt The table to filter.
   * @param event The event containing the target input element.
   */
  public filterGlobal(dt: Table, event: Event) {
    const target = event.target as HTMLInputElement;
    const value = target.value;
    dt.filterGlobal(value, "contains");
  }

  /**
   * Deletes a customer.
   * @param event The event that triggered the deletion.
   * @param assetId The id of the customer to be deleted.
   */
  public onDeleteClick(event: Event, assetId: string | undefined) {
    if (assetId) {
      this.confirmationService.confirm({
        target: event.currentTarget as EventTarget,
        message: this.translate.instant("gate_administration.asset_table.confirm_customer"),
        key: "global",
        accept: () => {
          this.assetService
            .deleteAsset(ASSET_TYPES.CUSTOMER, assetId)
            .pipe(first())
            .subscribe((res) => {
              if (res.success) {
                this.setCustomers(true);
              } else {
                // DELETE unsuccessful
              }
            });
        },
      });
    }
  }

  /**
   * Opens a dialog to list users of a customer.
   * @param {Customer} customer The customer for which the users are to be listed.
   */
  public onUsersClick(customer?: Customer) {
    // In case of a customer, open a dialog to list users. Otherwise opens userlist for whole tenant
    this.ref = this.dialogService.open(UserListComponent, {
      header: customer
        ? `${this.translate.instant("gate_administration.user_list.title")} - ${customer?.label}`
        : `${this.translate.instant("gate_administration.user_list.title")} - ${this.tenant?.label}`,
      modal: true,
      width: "65vw",
      contentStyle: { overflow: "auto" },
      breakpoints: {
        "960px": "75vw",
        "640px": "90vw",
      },
      data: { customerId: customer?.id, tenantId: this.tenant.id },
      closable: true,
    });
  }

  /**
   * Opens a dialog to edit settings the tenant can modify for himself
   */
  public onTenantSettingsClick() {
    this.authService
      .getAuthenticatedUser()
      .pipe(
        concatMap((user: User) => {
          if (user?.tenant?.id) {
            return this.tenantService.getTenant(user.tenant.id);
          } else {
            return of(null);
          }
        })
      )
      .pipe(first())
      .subscribe((tenant) => {
        if (tenant) {
          this.dialogService.open(TenantSettingsComponent, {
            header: this.translate.instant("gate_administration.tenant_settings.title"),
            data: tenant,
            modal: true,
            closable: true,
          });
        }
      });
  }

  /**
   * Gets inspection reports for the tenant of the authenticated user
   * @returns array of protocols, each with id, label, timestamp and url
   */
  private getProtocols() {
    this.authService
      .getAuthenticatedUser()
      .pipe(
        concatMap((user: User) => {
          if (user?.tenant?.id) {
            this.tenant = user.tenant;
            return this.mediaService.getMedia(ASSET_TYPES.TENANT, user.tenant.id, "INSPECTION_REPORT");
          } else {
            return of(null);
          }
        })
      )
      .pipe(first())
      .subscribe((protocols) => {
        if (protocols) {
          const mapedProtocols = protocols.map((p) => {
            return {
              id: p.id,
              label: p.additionalInfo?.description,
              timestamp: p.timestamp?.created_at,
              url: p.url,
            };
          });
          this.protocols.set(mapedProtocols);
        }
      });
  }

  /**
   * Opens a dialog for adding or editing a customer asset.
   * @param asset The asset to add or edit.
   * @param actionType Whether to add or edit the asset.
   * @returns A dialog reference, or undefined if the asset is undefined.
   */
  private openAssetForm(asset: BotevoAsset, actionType: string) {
    if (asset) {
      this.ref = this.dialogService.open<CustomerComponent>(CustomerComponent, {
        header: this.translate.instant("gate_administration.dialog_title." + actionType + ".customers"),
        modal: true,
        width: "50vw",
        contentStyle: { overflow: "auto" },
        breakpoints: {
          "960px": "75vw",
          "640px": "90vw",
        },
        data: { asset, actionType },
        closable: true,
      });
      this.ref?.onClose.subscribe({
        next: (success: boolean) => {
          if (success) {
            this.setCustomers(true);
          }
        },
      });
    }
  }
}
